import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['button', 'tooltip', 'icon']
  static values = {
    url: String,
    text: String,
    successText: { type: String, default: 'Copied!' },
    errorText: { type: String, default: 'Failed to copy' },
    timeout: { type: Number, default: 2000 }
  }

  connect() {
    this.resetTimeout = null
    
    if (!this.hasUrlValue) {
      this.urlValue = window.location.href
    }
  }

  disconnect() {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout)
    }
  }

  async copy(event) {
    event.preventDefault()
    
    const textToCopy = this.textValue || this.urlValue
    
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy)
        this.handleSuccess()
      } else {
        this.fallbackCopy(textToCopy)
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error)
      this.handleError()
    }
  }

  fallbackCopy(text) {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '-999999px'
    textArea.setAttribute('aria-hidden', 'true')
    
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    
    try {
      const successful = document.execCommand('copy')
      if (successful) {
        this.handleSuccess()
      } else {
        this.handleError()
      }
    } catch (error) {
      console.error('Fallback copy failed:', error)
      this.handleError()
    } finally {
      document.body.removeChild(textArea)
    }
  }

  handleSuccess() {
    this.showMessage(this.successTextValue, 'success')
    this.updateButtonState('success')
    
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout)
    }
    
    this.resetTimeout = setTimeout(() => {
      this.reset()
    }, this.timeoutValue)
  }

  handleError() {
    this.showMessage(this.errorTextValue, 'error')
    this.updateButtonState('error')
    
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout)
    }
    
    this.resetTimeout = setTimeout(() => {
      this.reset()
    }, this.timeoutValue)
  }

  showMessage(message, type) {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.textContent = message
      this.tooltipTarget.classList.remove('hidden', 'opacity-0')
      this.tooltipTarget.classList.add('opacity-100')
      
      if (type === 'success') {
        this.tooltipTarget.classList.remove('bg-red-500')
        this.tooltipTarget.classList.add('bg-green-500')
      } else if (type === 'error') {
        this.tooltipTarget.classList.remove('bg-green-500')
        this.tooltipTarget.classList.add('bg-red-500')
      }
      
      setTimeout(() => {
        this.tooltipTarget.classList.add('scale-100')
        this.tooltipTarget.classList.remove('scale-95')
      }, 10)
    }
  }

  updateButtonState(state) {
    if (this.hasButtonTarget) {
      if (state === 'success') {
        this.buttonTarget.classList.add('bg-green-500', 'text-white')
        this.buttonTarget.classList.remove('bg-gray-200', 'hover:bg-gray-300')
        
        if (this.hasIconTarget) {
          this.iconTarget.innerHTML = this.getCheckIcon()
        }
      } else if (state === 'error') {
        this.buttonTarget.classList.add('bg-red-500', 'text-white')
        this.buttonTarget.classList.remove('bg-gray-200', 'hover:bg-gray-300')
        
        if (this.hasIconTarget) {
          this.iconTarget.innerHTML = this.getErrorIcon()
        }
      }
    }
  }

  reset() {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.remove('opacity-100', 'scale-100')
      this.tooltipTarget.classList.add('opacity-0', 'scale-95')
      
      setTimeout(() => {
        this.tooltipTarget.classList.add('hidden')
      }, 200)
    }
    
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.remove('bg-green-500', 'bg-red-500', 'text-white')
      this.buttonTarget.classList.add('bg-gray-200', 'hover:bg-gray-300')
      
      if (this.hasIconTarget) {
        this.iconTarget.innerHTML = this.getLinkIcon()
      }
    }
  }

  getCheckIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
    </svg>`
  }

  getErrorIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
    </svg>`
  }

  getLinkIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
    </svg>`
  }

  async share(event) {
    event.preventDefault()
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: this.urlValue
        })
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error)
          this.copy(event)
        }
      }
    } else {
      this.copy(event)
    }
  }
}